import type { SetURLSearchParams } from 'react-router'

export type FeedChip =
	| { kind: 'all' }
	| { kind: 'bookmarked' }
	| { kind: 'category'; id: string }

export type FeedSort = 'newest' | 'price_asc' | 'price_desc'

export type FeedFilters = {
	searchInput: string
	chip: FeedChip
	sort: FeedSort
}

const SORTS = new Set<FeedSort>(['newest', 'price_asc', 'price_desc'])

export function parseFeedFilters(params: URLSearchParams): FeedFilters {
	const categoryId = params.get('c')
	const chip: FeedChip =
		params.get('b') === '1'
			? { kind: 'bookmarked' }
			: categoryId
				? { kind: 'category', id: categoryId }
				: { kind: 'all' }
	const sort = params.get('s')

	return {
		searchInput: params.get('q') ?? '',
		chip,
		sort: sort && SORTS.has(sort as FeedSort) ? (sort as FeedSort) : 'newest',
	}
}

/** Returns true if any search input, category/bookmark chip, or non-default sort is active. */
export function hasFeedFilters(filters: FeedFilters): boolean {
	return (
		filters.searchInput.trim() !== '' ||
		filters.chip.kind !== 'all' ||
		filters.sort !== 'newest'
	)
}

/**
 * Update the URL without adding a history entry for every search character or
 * filter change.
 */
export function replaceFeedFilters(
	setParams: SetURLSearchParams,
	filters: Partial<FeedFilters>,
) {
	setParams(
		(prev) => {
			const next = new URLSearchParams(prev)

			if ('searchInput' in filters) {
				if (filters.searchInput) next.set('q', filters.searchInput)
				else next.delete('q')
			}

			if ('chip' in filters) {
				next.delete('b')
				next.delete('c')
				if (filters.chip?.kind === 'bookmarked') next.set('b', '1')
				if (filters.chip?.kind === 'category') next.set('c', filters.chip.id)
			}

			if ('sort' in filters) {
				if (filters.sort && filters.sort !== 'newest') next.set('s', filters.sort)
				else next.delete('s')
			}

			return next
		},
		{ replace: true },
	)
}
